import React, { useEffect, useState } from 'react'
import useTimer, { CountdownDelay } from '../hooks/useTimer'
import getTimeRemaining, { TimeRemaining } from '../util/getTimeRemaining'

export type CountdownTimerProps = {
  date: Date
  getFormattedDate?: (timeRemaining: TimeRemaining, units: Units) => string
  getDelay?: (timeRemaining: TimeRemaining) => CountdownDelay
  units?: Units
}

export type Units = {
  days: string
  day: string
  hours: string
  hour: string
  minutes: string
  minute: string
  seconds: string
  second: string
}

export const defaultUnits: Units = {
  days: 'd',
  day: 'd',
  hours: 'hrs',
  hour: 'hr',
  minutes: 'mins',
  minute: 'min',
  seconds: 'secs',
  second: 'sec',
}

export const defaultGetFormattedDate: CountdownTimerProps['getFormattedDate'] =
  (timeRemaining, units) => {
    const { total, days, hours, minutes, seconds } = timeRemaining

    if (days > 0) {
      return `${days} ${days === 1 ? units.day : units.days} ${hours} ${
        hours === 1 ? units.hour : units.hours
      } `
    }

    if (hours > 0) {
      return `${hours} ${hours === 1 ? units.hour : units.hours} ${minutes}${
        minutes === 1 ? units.minute : units.minutes
      }`
    }

    if (total <= 0) {
      return 'Now'
    }

    return `${minutes} ${units.minutes} ${seconds} ${units.seconds}`
  }

export const ONE_HOUR = 60 * 60 * 1000
export const ONE_MINUTE = 60 * 1000
export const ONE_SECOND = 1000

export const defaultGetDelay: CountdownTimerProps['getDelay'] = ({
  days,
  hours,
  total,
}) => {
  const tickByHour = days > 0
  if (tickByHour) {
    return ONE_HOUR
  }

  const tickByMinute = hours > 0
  if (tickByMinute) {
    return ONE_MINUTE
  }

  const noTick = total <= 0
  if (noTick) {
    return 'disable-timer'
  }

  return 1000
}

const CountdownTimer = ({
  date,
  getFormattedDate = defaultGetFormattedDate,
  getDelay = defaultGetDelay,
  units = defaultUnits,
}: CountdownTimerProps) => {
  const getLatestCountdown = () => {
    return getTimeRemaining(new Date(), date)
  }

  const [timeRemaining, setTimeRemaining] = useState(getLatestCountdown())

  useEffect(() => {
    setTimeRemaining(getTimeRemaining(new Date(), date))
  }, [date])

  const updateTimeRemaining = () => {
    setTimeRemaining(getLatestCountdown())
  }

  const delay = getDelay(timeRemaining)

  useTimer({ onTick: updateTimeRemaining, delay }, [date, delay])

  return <>{getFormattedDate(timeRemaining, units)}</>
}

export default CountdownTimer
